import { shouldShowPriority1Notification } from "./notificationPriority";

export const PRIORITY_POPUP_FRESHNESS_MS = 5 * 60 * 1000;

const INFORMATIVE_TYPES = new Set([
  "attendance_confirmed",
  "presence_confirmed",
  "conflict_resolved",
  "single_challenge_accepted",
  "single_challenge_declined",
  "admin_message",
  "tournament_cancelled_refund",
  "reward_tournament_win",
  "creator_tournament_request_edited",
  "creator_tournament_request_approved",
  "creator_tournament_request_rejected",
  "group_stage_qualified",
  "group_stage_eliminated",
  "prize_payout_completed",
  "prize_physical_shipped",
]);

const MATCH_CONTEXT_TYPES = new Set([
  "opponent_found",
  "tournament_check_in_open",
  "tournament_check_in_reminder",
  "tournament_match_ready",
  "result_reported",
  "attendance_confirmed",
  "presence_confirmation_required",
  "presence_confirmed",
  "conflict_opened",
  "conflict_resolved",
  "upload_deadline_warning",
]);

const SUPERSESSION_RANK = Object.freeze({
  opponent_found: 10,
  tournament_check_in_open: 20,
  tournament_check_in_reminder: 30,
  tournament_match_ready: 40,
  result_reported: 50,
  presence_confirmation_required: 60,
  attendance_confirmed: 70,
  presence_confirmed: 70,
  conflict_opened: 80,
  upload_deadline_warning: 90,
  conflict_resolved: 100,
  single_challenge_invite: 10,
  challenge_expiring: 20,
  single_challenge_accepted: 30,
  single_challenge_declined: 30,
  prize_fulfillment_needs_action: 10,
  prize_payout_completed: 20,
  prize_physical_shipped: 20,
  tournament_one_hour_reminder: 10,
  tournament_starting: 20,
});

export function timestampToMillis(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getPriorityNotificationContextKey(notification = {}) {
  const type = String(notification.type || "");
  const matchId = String(notification.singleMatchId || notification.matchId || "");
  if (matchId && MATCH_CONTEXT_TYPES.has(type)) {
    return `match:${notification.singleMatchId ? "single" : String(notification.tournamentId || "tournament")}:${matchId}`;
  }
  if (notification.challengeId) return `challenge:${notification.challengeId}`;
  if (notification.conflictId) return `conflict:${notification.conflictId}`;
  if (type.startsWith("prize_")) return `prize:${notification.relatedId || notification.tournamentId || notification.id}`;
  if (type.startsWith("creator_tournament_request_")) return `request:${notification.relatedId || notification.id}`;
  if (type === "group_stage_ruling_required") {
    return `ruling:${notification.tournamentId || ""}:${notification.decisionId || notification.groupId || notification.id}`;
  }
  if (notification.tournamentId) return `tournament:${notification.tournamentId}`;
  return `notification:${notification.id || type}`;
}

function result(eligible, reason, notification, overrides = {}) {
  return {
    eligible,
    reason,
    contextKey: getPriorityNotificationContextKey(notification),
    supersessionRank: SUPERSESSION_RANK[notification?.type] || 0,
    ...overrides,
  };
}

function isFresh(notification, nowMs) {
  const createdAtMs = timestampToMillis(notification?.createdAt);
  return createdAtMs > 0 && nowMs - createdAtMs <= PRIORITY_POPUP_FRESHNESS_MS;
}

function isBefore(value, nowMs) {
  const millis = timestampToMillis(value);
  return millis > nowMs;
}

function ownCheckInMissing(match = {}, currentUid = "") {
  const slot = match.player1Uid === currentUid ? "player1" : match.player2Uid === currentUid ? "player2" : "";
  if (!slot) return false;
  const entry = match.checkIns?.[slot];
  return !(entry?.checkedInAt || entry?.confirmedAt || entry === true);
}

function isPlayableMatchStatus(status) {
  return ["pending", "resume_pending", "ready", "playing"].includes(String(status || "").toLowerCase());
}

export function getPresencePriorityPopupStatus({
  notification = {},
  currentUid,
  state = {},
  nowMs = Date.now(),
} = {}) {
  const match = state.match || {};
  const presence = match.postReadyPresence || {};
  const notificationCycle = Number(notification.presenceCycle || 0);
  const presenceCycle = Number(presence.cycle || 0);
  const notificationLifecycle = Number(notification.matchLifecycleVersion || 0);
  const matchLifecycle = Number(match.matchLifecycleVersion || 0);
  const notificationCreatedAtMs = timestampToMillis(notification.createdAt);
  const requestedAtMs = timestampToMillis(presence.requestedAt);
  const deadlineMs = timestampToMillis(presence.responseDeadline);
  const currentLifecycle = (!notificationCycle || !presenceCycle || notificationCycle === presenceCycle) &&
    (!notificationLifecycle || !matchLifecycle || notificationLifecycle === matchLifecycle) &&
    (notificationCycle || !notificationCreatedAtMs || !requestedAtMs || notificationCreatedAtMs >= requestedAtMs - 5_000);
  const open = state.matchFound === true &&
    String(presence.status || "").toLowerCase() === "open" &&
    presence.targetUid === currentUid &&
    !presence.confirmedAt && !presence.conflictId && !match.conflictId &&
    isPlayableMatchStatus(match.status) && currentLifecycle;

  if (!open) return { status: "obsolete", deadlineMs };
  if (!deadlineMs || deadlineMs <= nowMs) return { status: "expired", deadlineMs };
  return { status: "active", deadlineMs };
}

export function getPresencePriorityCountdown(deadline, nowMs = Date.now()) {
  const deadlineMs = timestampToMillis(deadline);
  const remainingMs = Math.max(0, deadlineMs - nowMs);
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    deadlineMs,
    remainingMs,
    expired: deadlineMs <= 0 || remainingMs <= 0,
    text: `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
  };
}

export function getPriorityNotificationPopupEligibility({
  notification,
  currentUid,
  state = {},
  nowMs = Date.now(),
  processed = false,
} = {}) {
  if (!notification?.id || notification.isRead === true || !shouldShowPriority1Notification(notification)) {
    return result(false, "not_unread_priority", notification || {});
  }
  if (processed) return result(false, "already_processed_in_session", notification);

  const type = String(notification.type || "");
  const match = state.match || {};
  const tournament = state.tournament || {};
  const challenge = state.challenge || {};
  const conflict = state.conflict || {};

  if (type === "presence_confirmation_required") {
    const presenceStatus = getPresencePriorityPopupStatus({ notification, currentUid, state, nowMs });
    return result(
      presenceStatus.status === "active",
      presenceStatus.status === "active" ? "presence_open" : `presence_${presenceStatus.status}`,
      notification,
      { presenceStatus: presenceStatus.status, deadlineMs: presenceStatus.deadlineMs }
    );
  }

  if (type === "tournament_check_in_open" || type === "tournament_check_in_reminder") {
    const eligible = state.matchFound === true && String(match.status || "").toLowerCase() === "check_in_open" &&
      ownCheckInMissing(match, currentUid) && isBefore(match.checkInDeadline, nowMs);
    return result(eligible, eligible ? "check_in_open" : "check_in_obsolete", notification);
  }

  if (type === "tournament_match_ready" || type === "opponent_found") {
    const eligible = state.matchFound === true && isPlayableMatchStatus(match.status);
    return result(eligible, eligible ? "match_playable" : "match_obsolete", notification);
  }

  if (type === "result_reported") {
    const status = String(match.status || "").toLowerCase();
    const isV2Response = ["pending", "resume_pending"].includes(status) &&
      match.hasOpponentDualReport === true &&
      match.hasOwnDualReport !== true &&
      Number(notification.reportCycle) === Number(match.dualReportCycle);
    const isLegacyResponse = status === "reported";
    const deadline = isV2Response
      ? match.dualResponseDeadline
      : match.responseDeadline;
    const eligible = state.matchFound === true &&
      (isV2Response || isLegacyResponse) &&
      !match.conflictId &&
      isBefore(deadline, nowMs);
    return result(eligible, eligible ? "report_response_open" : "report_obsolete", notification);
  }

  if (type === "conflict_opened") {
    const evidenceOpen = state.conflictFound === true && conflict.status === "open" && !conflict.evidenceClosedAt && isBefore(conflict.evidenceDeadline, nowMs);
    const eligible = evidenceOpen || (state.conflictFound === true && conflict.status !== "resolved" && isFresh(notification, nowMs));
    return result(eligible, evidenceOpen ? "conflict_evidence_open" : eligible ? "conflict_fresh" : "conflict_obsolete", notification);
  }

  if (type === "upload_deadline_warning") {
    const eligible = state.conflictFound === true && conflict.status === "open" && !conflict.evidenceClosedAt &&
      !conflict.ownSubmitted && isBefore(conflict.evidenceDeadline, nowMs);
    return result(eligible, eligible ? "evidence_action_open" : "evidence_obsolete", notification);
  }

  if (type === "single_challenge_invite" || type === "challenge_expiring") {
    const expiresAt = challenge.expiresAt || notification.expiresAt;
    const eligible = state.challengeFound === true && String(challenge.status || "").toLowerCase() === "pending" && isBefore(expiresAt, nowMs);
    return result(eligible, eligible ? "challenge_pending" : "challenge_obsolete", notification);
  }

  if (type === "tournament_starting" || type === "tournament_one_hour_reminder") {
    const eligible = state.tournamentFound === true && String(tournament.status || "").toLowerCase() === "open" &&
      !tournament.matchesGenerated && isBefore(tournament.startAt, nowMs);
    return result(eligible, eligible ? "tournament_not_started" : "tournament_started", notification);
  }

  if (type === "group_stage_ruling_required") {
    const eligible = state.tournamentFound === true && state.rulingOpen === true;
    return result(eligible, eligible ? "ruling_open" : "ruling_complete", notification);
  }

  if (type === "prize_fulfillment_needs_action") {
    const eligible = state.prizeRequestFound === true && String(state.prizeRequest?.status || "").toLowerCase() === "needs_action";
    return result(eligible, eligible ? "prize_action_open" : "prize_action_complete", notification);
  }

  if (type === "presence_confirmed" || type === "attendance_confirmed") {
    const eligible = isFresh(notification, nowMs) && state.matchFound === true && isPlayableMatchStatus(match.status);
    return result(eligible, eligible ? "fresh_playable_presence_info" : "presence_info_obsolete", notification);
  }

  if (type === "conflict_resolved") {
    const status = String(conflict.status || "").toLowerCase();
    const eligible = isFresh(notification, nowMs) && state.conflictFound === true && ["resolved", "closed"].includes(status);
    return result(eligible, eligible ? "fresh_resolved_conflict" : "conflict_resolution_obsolete", notification);
  }

  if (type === "single_challenge_accepted") {
    const eligible = isFresh(notification, nowMs) && state.matchFound === true && isPlayableMatchStatus(match.status);
    return result(eligible, eligible ? "fresh_accepted_challenge" : "accepted_challenge_obsolete", notification);
  }

  if (type === "prize_payout_completed" || type === "prize_physical_shipped") {
    const eligible = isFresh(notification, nowMs) && state.prizeRequestFound === true &&
      String(state.prizeRequest?.status || "").toLowerCase() === "completed";
    return result(eligible, eligible ? "fresh_completed_prize" : "prize_completion_obsolete", notification);
  }

  if (type === "creator_tournament_request_changes_requested" && state.creatorRequestStatus) {
    const eligible = ["changes_requested", "needs_changes"].includes(String(state.creatorRequestStatus).toLowerCase());
    return result(eligible, eligible ? "creator_action_open" : "creator_action_complete", notification);
  }

  if (INFORMATIVE_TYPES.has(type)) {
    const eligible = isFresh(notification, nowMs);
    return result(eligible, eligible ? "fresh_informative" : "stale_informative", notification);
  }

  const eligible = isFresh(notification, nowMs);
  return result(eligible, eligible ? "fresh_fallback" : "stale_fallback", notification);
}

export function selectPriorityNotificationPopup({ notifications = [], statesById = {}, currentUid, processedIds, nowMs = Date.now() } = {}) {
  const eligible = notifications
    .map((notification) => ({
      notification,
      eligibility: getPriorityNotificationPopupEligibility({
        notification,
        currentUid,
        state: statesById[notification.id] || {},
        nowMs,
        processed: processedIds?.has(notification.id) === true,
      }),
    }))
    .filter((entry) => entry.eligibility.eligible);

  const latestByContext = new Map();
  eligible.forEach((entry) => {
    const key = entry.eligibility.contextKey;
    const previous = latestByContext.get(key);
    const rankDiff = entry.eligibility.supersessionRank - (previous?.eligibility.supersessionRank || 0);
    const timeDiff = timestampToMillis(entry.notification.createdAt) - timestampToMillis(previous?.notification.createdAt);
    if (!previous || rankDiff > 0 || (rankDiff === 0 && timeDiff > 0)) latestByContext.set(key, entry);
  });

  const selected = [...latestByContext.values()]
    .sort((a, b) => timestampToMillis(b.notification.createdAt) - timestampToMillis(a.notification.createdAt))[0] || null;
  if (!selected) return null;
  return {
    ...selected,
    supersededNotificationIds: eligible
      .filter((entry) => entry.eligibility.contextKey === selected.eligibility.contextKey)
      .map((entry) => entry.notification.id),
  };
}
